import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useAuth } from './auth/AuthContext';
import { AREA_HOME, areaFor, homeFor, type Area } from './auth/roles';
import { AppShell } from './components/layout/AppShell';
import { PageLoading } from './components/ui/States';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ActivityLog } from './pages/staff/ActivityLog';
import { Creators } from './pages/staff/Creators';
import { Overview } from './pages/staff/Overview';
import { ReviewQueue } from './pages/staff/ReviewQueue';
import { Tenants } from './pages/staff/Tenants';
import { Performance } from './pages/studio/Performance';
import { TitleCatalog } from './pages/studio/TitleCatalog';
import { ApiKeys } from './pages/developer/ApiKeys';
import { Playground } from './pages/developer/Playground';
import { Usage } from './pages/developer/Usage';

/** Sends someone to their own area if they wander into another one. The server enforces this too. */
function OnlyFor({ area, children }: { area: Area; children: ReactElement }) {
  const { user } = useAuth();
  if (!user) return null;
  return areaFor(user.role) === area ? children : <Navigate to={homeFor(user.role)} replace />;
}

export function App() {
  const { status, user, mustChangePassword } = useAuth();

  if (status === 'loading') return <PageLoading label="Signing you in…" />;
  if (status === 'anonymous' || !user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  if (mustChangePassword) return <ChangePasswordPage />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Navigate to={homeFor(user.role)} replace />} />

        <Route path={AREA_HOME.staff} element={<OnlyFor area="staff"><Overview /></OnlyFor>} />
        <Route path="/staff/review" element={<OnlyFor area="staff"><ReviewQueue /></OnlyFor>} />
        <Route path="/staff/catalog" element={<OnlyFor area="staff"><TitleCatalog scope="all" /></OnlyFor>} />
        <Route path="/staff/creators" element={<OnlyFor area="staff"><Creators /></OnlyFor>} />
        <Route path="/staff/tenants" element={<OnlyFor area="staff"><Tenants /></OnlyFor>} />
        <Route path="/staff/audit" element={<OnlyFor area="staff"><ActivityLog /></OnlyFor>} />

        <Route path={AREA_HOME.studio} element={<OnlyFor area="studio"><TitleCatalog scope="mine" /></OnlyFor>} />
        <Route path="/studio/analytics" element={<OnlyFor area="studio"><Performance /></OnlyFor>} />

        <Route path={AREA_HOME.developer} element={<OnlyFor area="developer"><ApiKeys /></OnlyFor>} />
        <Route path="/developer/usage" element={<OnlyFor area="developer"><Usage /></OnlyFor>} />
        <Route path="/developer/playground" element={<OnlyFor area="developer"><Playground /></OnlyFor>} />

        <Route path="*" element={<Navigate to={homeFor(user.role)} replace />} />
      </Route>
    </Routes>
  );
}
